// Mock + API stub for the Fintos AI screening service
//
// In production this would talk to a real Fintos AI server. For the demo,
// the request/response shape mirrors a real fetch() call so swapping the
// implementation later is a one-line change.

import { apiProductCatalog } from '../data/productConfigData';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const generateScreeningId = () =>
  `SCR-${Date.now().toString(36).toUpperCase().slice(-6)}-${Math.random().toString(36).toUpperCase().slice(2, 5)}`;

// ────────────────────────────────────────────────────────────────
// Internal scoring engine — matches a customer profile against a product
// ────────────────────────────────────────────────────────────────

// Returns 0-100 score. Higher = better fit.
function scoreProduct(product, profile, loanAmount, tenureMonths) {
  let score = 50; // baseline
  const reasons = [];

  const monthlyIncome = Number(profile.monthlyIncome) || 0;

  // 1) Income vs minimum required (rough heuristic per category)
  const minIncomeByCategory = {
    personal_loan: 2000,
    credit_card: 2500,
    housing_loan: 5000,
    property: 5000,
    business_loan: 8000,
    government_loan: 1500,
    micro_loan: 800,
  };
  const minIncome = minIncomeByCategory[product.category] || 2000;
  if (monthlyIncome >= minIncome * 1.5) {
    score += 20;
    reasons.push(`Income exceeds minimum (RM ${minIncome.toLocaleString()})`);
  } else if (monthlyIncome >= minIncome) {
    score += 5;
    reasons.push(`Meets minimum income (RM ${minIncome.toLocaleString()})`);
  } else {
    score -= 30;
    reasons.push(`Income below minimum required (RM ${minIncome.toLocaleString()})`);
  }

  // 2) DSR estimate (if loan amount and tenure are provided)
  if (loanAmount > 0 && tenureMonths > 0) {
    const monthlyInstalment = loanAmount / tenureMonths * 1.05; // ~5% markup
    const affordableInstalment = monthlyIncome * 0.6;
    const dsrRatio = affordableInstalment / monthlyInstalment;
    if (dsrRatio >= 1.5) {
      score += 15;
      reasons.push('Low debt-service ratio — strong affordability');
    } else if (dsrRatio >= 1.0) {
      score += 5;
      reasons.push('Affordability within bank tolerance');
    } else {
      score -= 25;
      reasons.push('Debt-service ratio too high for requested amount');
    }
  }

  // 3) Employment type fit
  const empGroup = profile.employmentGroup || 'salaried';
  if (empGroup === 'salaried') {
    if (['government', 'glc', 'army'].includes(profile.employmentType)) {
      score += 10;
      reasons.push('Government / GLC employment is highly favoured');
    } else {
      score += 5;
      reasons.push('Salaried employment is standard-eligible');
    }
  } else if (empGroup === 'self_employed') {
    score -= 5;
    reasons.push('Self-employed requires additional documentation');
    if (product.category === 'business_loan') {
      score += 15;
      reasons.push('Self-employed is a strong fit for business loans');
    }
  } else if (empGroup === 'commission') {
    score -= 10;
    reasons.push('Commission income is treated cautiously');
  }

  // 4) Working vintage (years at current employer)
  const vintage = Number(profile.workingVintage) || 0;
  if (vintage >= 3) {
    score += 5;
    reasons.push(`${vintage}+ years employment vintage`);
  } else if (vintage < 1) {
    score -= 10;
    reasons.push('Less than 1 year working vintage');
  }

  // 5) Age check (estimated from IC number — first 6 chars = YYMMDD)
  if (profile.icNumber && profile.icNumber.length >= 6) {
    const yy = parseInt(profile.icNumber.slice(0, 2), 10);
    const fullYear = yy >= 30 ? 1900 + yy : 2000 + yy;
    const age = new Date().getFullYear() - fullYear;
    if (age >= 25 && age <= 55) {
      score += 5;
    } else if (age < 21 || age > 65) {
      score -= 15;
      reasons.push(`Age ${age} outside typical bank approval range`);
    }
  }

  // Clamp 0-100
  score = Math.max(0, Math.min(100, score));

  // Status mapping
  let status;
  let statusLabel;
  if (score >= 70) {
    status = 'approval';
    statusLabel = 'Eligible — pre approved';
  } else if (score >= 40) {
    status = 'refer';
    statusLabel = 'Borderline — manual review recommended';
  } else {
    status = 'fail';
    statusLabel = 'Bank criteria not met';
  }

  return {
    productCode: product.productCode,
    category: product.category,
    lenderName: product.lenderName,
    productName: product.productName,
    status,
    statusLabel,
    score: Math.round(score),
    reason: reasons.join('. '),
  };
}

// ────────────────────────────────────────────────────────────────
// Public API (looks like a real fetch() call)
// ────────────────────────────────────────────────────────────────

/**
 * Request screening for a customer against a list of products.
 *
 * In production:
 *   return fetch('https://api.fintos.ai/v1/screening', {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify({ customerProfile, productCodes, loanAmount, tenureMonths }),
 *   }).then((r) => r.json());
 *
 * Demo: simulate ~2.5s latency and run mock scoring locally.
 */
export async function requestScreening({ customerProfile, productCodes, loanAmount = 0, tenureMonths = 36 }) {
  await delay(2500);

  const productsToScore = productCodes && productCodes.length
    ? apiProductCatalog.filter((p) => productCodes.includes(p.productCode))
    : apiProductCatalog;

  const results = productsToScore.map((p) => scoreProduct(p, customerProfile, loanAmount, tenureMonths));

  return {
    screeningId: generateScreeningId(),
    completedAt: new Date().toISOString(),
    customerProfile: {
      name: customerProfile.fullName,
      icNumber: customerProfile.icNumber,
      monthlyIncome: customerProfile.monthlyIncome,
    },
    loanAmount,
    tenureMonths,
    results,
    summary: {
      total: results.length,
      approval: results.filter((r) => r.status === 'approval').length,
      refer: results.filter((r) => r.status === 'refer').length,
      fail: results.filter((r) => r.status === 'fail').length,
    },
  };
}

/**
 * Poll the status of a previously-submitted screening.
 *
 * In production:
 *   return fetch(`https://api.fintos.ai/v1/screening/${screeningId}`).then((r) => r.json());
 *
 * Demo: returns 'completed' immediately (since requestScreening already runs synchronously).
 */
export async function pollScreeningStatus(screeningId) {
  return {
    screeningId,
    status: 'completed',
    progress: 100,
  };
}

/**
 * Compute a quick eligibility hint without calling the screening service.
 * Used in the loan-config step to show partners an estimated max loan + DSR.
 */
export function quickEligibilityHint(profile, tenureMonths = 36) {
  const monthlyIncome = Number(profile.monthlyIncome) || 0;
  if (monthlyIncome <= 0) return null;
  const affordableInstalment = monthlyIncome * 0.6;
  const estimatedMaxLoan = Math.floor((affordableInstalment * tenureMonths) / 1.05);
  return {
    monthlyIncome,
    affordableInstalment: Math.round(affordableInstalment),
    estimatedMaxLoan,
    tenureMonths,
  };
}
